import { observable, action } from 'mobx';

import {
  Section,
  UserProfile,
  CustomMarkerDefinition,
  CustomMarkerSection,
  RangeOverride,
  practitionerSectionKey,
  renameDefinitionsSection,
  renameInDefaultTestSections,
} from '../models';
import { MarkersOutsideOptimalDisplay } from '../models/UserProfile';
import { UnitSystem } from '../models/units/unitConversions';
import { LoadingStore } from './loading-store';
import { UserProfileStore } from './user-profile-store';

export class UserProfileUIStore {
  @observable currentUserProfile: UserProfile;
  @observable logoUpdateTrigger: number = 0; // Trigger for logo updates

  constructor(
    private userProfileStore: UserProfileStore,
    private loadingStore: LoadingStore,
  ) {}

  /**
   * One request in flight at a time.
   *
   * Not a session cache — a sequential call still refetches, which several
   * pages rely on. It collapses CONCURRENT calls, which is what happens on
   * every page that needs both the profile and the range table: the range
   * table now waits for the profile (My Ranges overrides are applied to it),
   * and the page asks for the profile itself, so without this the PDF window
   * fired two GETs for the same profile on open.
   */
  private profileRequest?: Promise<UserProfile>;

  @action loadCurrentUserProfile = (): Promise<UserProfile> => {
    if (this.profileRequest) {
      return this.profileRequest;
    }
    this.loadingStore.appIsBusy();
    this.profileRequest = this.userProfileStore
      .loadCurrentUserProfile()
      .then((profile: UserProfile) => {
        this.currentUserProfile = profile;
        return profile;
      });
    const settled = () => {
      this.profileRequest = undefined;
      this.loadingStore.appIsNotBusy();
    };
    // Cleared either way, so a failed load does not wedge every later call on
    // a rejected promise.
    this.profileRequest.then(settled, settled);
    return this.profileRequest;
  };

  @action saveCurrentUserProfile = async (): Promise<UserProfile> => {
    this.loadingStore.appIsBusy();

    // finally, not a trailing call: a rejected save used to skip appIsNotBusy()
    // and leave the spinner modal up over a locked page, which is the worst
    // possible way to report a failure — it looks like the app hung rather than
    // like something needs fixing.
    try {
      this.currentUserProfile = await this.userProfileStore.updateUserProfile(
        this.currentUserProfile,
      );
      return this.currentUserProfile;
    } finally {
      this.loadingStore.appIsNotBusy();
    }
  };

  @action updateLogo = (logoUrl: string): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.customLogoUrl = logoUrl;
      this.logoUpdateTrigger = Date.now(); // Trigger re-render
    }
  };

  @action removeLogo = (): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.customLogoUrl = '';
      this.logoUpdateTrigger = Date.now(); // Trigger re-render
    }
  };

  @action updatePreparedBy = (value: string): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.preparedBy = value;
    }
  };

  /**
   * The cover-page heading on each PDF, edited in Report Settings.
   *
   * Stored trimmed and stored EMPTY rather than deleted when it is cleared: an
   * empty string resolves to the shipped default the same way a missing field
   * does (see models/reportTitles), and leaving the key in place means the PUT
   * carries the practitioner's clearing to Dynamo instead of the field simply
   * going missing from the body and the old title surviving the save.
   */
  @action updateReportTitles = (
    analysisTitle: string,
    comparisonTitle: string,
  ): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.analysisReportTitle = (analysisTitle || '').trim();
      this.currentUserProfile.comparisonReportTitle = (
        comparisonTitle || ''
      ).trim();
    }
  };

  /**
   * The practitioner's own name, edited on the Profile page.
   *
   * This writes the profile, not the Cognito user. The two can diverge: the
   * name Cognito holds is whatever was typed at sign-up, and nothing writes
   * back to it. That is deliberate for now, because the pool's app client may
   * not have given_name/family_name marked writable (they are only populated in
   * prod, see UserProfile), and a save that throws for half the practitioners
   * is worse than one the app fully owns. Navigation reads the profile first
   * and falls back to the session token, so an edit here shows up immediately.
   */
  @action updateName = (firstName: string, lastName: string): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.firstName = firstName;
      this.currentUserProfile.lastName = lastName;
    }
  };

  /**
   * The two Create Account questions, edited on the Profile page.
   *
   * Like updateName, this writes the profile only. The Cognito attributes
   * (custom:practitioner_type, custom:referral_source) keep the answer given
   * at sign-up, and that is also the value prod's postSignUp sent on to
   * ActiveCampaign, so a change here reaches neither of them.
   */
  @action updateSignUpAnswers = (
    practitionerType: string,
    referralSource: string,
  ): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.practitionerType = practitionerType;
      this.currentUserProfile.referralSource = referralSource;
    }
  };

  /**
   * Add or remove an outside system's permission to write results into this
   * account. Idempotent in both directions, so a double click cannot produce a
   * duplicate entry or a half-removed one.
   *
   * A NEW array rather than push/splice: the profile is handed to observers by
   * reference, and mutating the list in place leaves a checkbox reading the old
   * contents until something else forces a render.
   */
  @action updateLabIntegration = (
    integration: string,
    enabled: boolean,
  ): void => {
    if (!this.currentUserProfile) {
      return;
    }
    const current = this.currentUserProfile.enabledLabIntegrations || [];
    const without = current.filter(value => value !== integration);
    this.currentUserProfile.enabledLabIntegrations = enabled
      ? [...without, integration]
      : without;
  };

  @action updateEnabledSupplementBrands = (brands: string[]): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.enabledSupplementBrands = brands;
    }
  };

  @action updateExcludedFromPdfSections = (sections: Section[]): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.excludedFromPdfSections = sections;
    }
  };

  @action updateExcludedInsightsFromPdfSections = (sections: Section[]): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.excludedInsightsFromPdfSections = sections;
    }
  };

  @action updateMarkersOutsideOptimalDisplay = (mode: MarkersOutsideOptimalDisplay): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.markersOutsideOptimalDisplay = mode;
    }
  };

  @action updateShowOnlySectionSignals = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showOnlySectionSignals = value;
    }
  };

  @action updateShowOnlyImmuneSectionSignals = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showOnlyImmuneSectionSignals = value;
    }
  };

  @action updateShowImmuneAbsolutes = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showImmuneAbsolutes = value;
    }
  };

  @action updateShowPreviousTestMarkersInComparison = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showPreviousTestMarkersInComparison = value;
    }
  };

  @action updateShowComparisonNotesOnScreen = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showComparisonNotesOnScreen = value;
    }
  };

  @action updateShowComparisonNotesInPdf = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showComparisonNotesInPdf = value;
    }
  };

  @action updateShowComparisonInsightsNotesInPdf = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showComparisonInsightsNotesInPdf = value;
    }
  };

  @action updateShowComparisonOverallProgressInPdf = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showComparisonOverallProgressInPdf = value;
    }
  };

  @action updateHideAlarmColumnsInComparison = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.hideAlarmColumnsInComparison = value;
    }
  };

  @action updateHideLabColumnsInComparison = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.hideLabColumnsInComparison = value;
    }
  };

  @action updateUnitSystem = (value: UnitSystem): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.unitSystem = value;
    }
  };

  @action updateCustomMarkerLibrary = (
    library: CustomMarkerDefinition[],
  ): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.customMarkerLibrary = library;
    }
  };

  /**
   * Store the practitioner's own reference ranges and persist them.
   *
   * Saves rather than staging, because nothing else on the My Ranges page is
   * going to save for it, and because the change is retroactive: the dialog
   * the practitioner just confirmed said this applies to every report, so the
   * write has to be the thing that button does.
   *
   * The whole list is replaced, not merged. The page hands over the list it
   * was showing, which is the only state that can be reasoned about.
   */
  @action updateRangeOverrides = (
    overrides: RangeOverride[],
  ): Promise<UserProfile> => {
    if (!this.currentUserProfile) {
      return Promise.resolve(this.currentUserProfile);
    }
    // Put the old list back if the PUT fails. The profile in memory is what
    // the page renders AND what effectiveRanges reads, so leaving a failed
    // edit on it would show the practitioner their new ranges, grade the next
    // report on them, and still be saying the save did not happen.
    const previous = this.currentUserProfile.rangeOverrides;
    this.currentUserProfile.rangeOverrides = overrides;
    return this.saveCurrentUserProfile().catch((error: any) => {
      if (this.currentUserProfile) {
        this.currentUserProfile.rangeOverrides = previous;
      }
      throw error;
    });
  };

  @action updateCustomMarkerSections = (
    sections: CustomMarkerSection[],
  ): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.customMarkerSections = sections;
    }
  };

  @action updateDefaultTestSections = (sections: string[]): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.defaultTestSections = sections;
    }
  };

  @action updateDefaultTestMarkerIds = (markerIds: string[]): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.defaultTestMarkerIds = markerIds;
    }
  };

  @action updateExpandedTestSections = (sections: string[]): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.expandedTestSections = sections;
    }
  };

  /**
   * Add a practitioner-named report section and persist it.
   *
   * Called from the Section dropdown wherever a custom marker is filed, so a
   * section created mid-entry is on the list for the next client. A duplicate
   * name is a no-op rather than an error: the dropdown already refuses one, and
   * two clicks on a slow connection must not produce two sections.
   */
  @action addCustomMarkerSection = (name: string, id: string): Promise<any> => {
    const profile = this.currentUserProfile;
    const trimmed = (name || '').trim();
    if (!profile || !trimmed) {
      return Promise.resolve();
    }
    const existing = profile.customMarkerSections || [];
    if (
      existing.some(
        section =>
          practitionerSectionKey(section.name) === practitionerSectionKey(trimmed),
      )
    ) {
      return Promise.resolve();
    }
    profile.customMarkerSections = [
      ...existing,
      new CustomMarkerSection({ id, name: trimmed }),
    ];
    return this.saveCurrentUserProfile();
  };

  /**
   * Rename a section in the practitioner's list, matched on its current name.
   *
   * Only the list. Markers store the name they were saved with, so a report
   * already issued to a client keeps the section heading it was issued under —
   * the caller renames the rows on the test in front of them, and older tests
   * keep theirs until they are opened. Adds the section if it is not on the
   * list yet, which is what happens when the typo was never saved there.
   */
  @action renameCustomMarkerSection = (
    from: string,
    to: string,
    newId: string,
  ): Promise<any> => {
    const profile = this.currentUserProfile;
    const trimmed = (to || '').trim();
    if (!profile || !trimmed) {
      return Promise.resolve();
    }
    const fromKey = practitionerSectionKey(from);
    const existing = profile.customMarkerSections || [];
    const match = existing.find(
      section => practitionerSectionKey(section.name) === fromKey,
    );
    if (!match) {
      return this.addCustomMarkerSection(trimmed, newId);
    }
    profile.customMarkerSections = existing.map(section =>
      section === match
        ? new CustomMarkerSection({ id: section.id, name: trimmed })
        : section,
    );
    // The library's definitions carry the name too, and the My Markers page
    // renames them when a section is renamed there. Skipping them here left
    // the list saying one spelling and the definitions another, so a section
    // added to the NEXT test arrived filed under a name the list no longer
    // has — its Section dropdown fell back to Other Lab Results while its
    // card, which groups case-insensitively, still read the section's name.
    profile.customMarkerLibrary = renameDefinitionsSection(
      profile.customMarkerLibrary,
      from,
      trimmed,
    );
    // BOTH per-section settings store the NAME, so a rename that skipped either
    // would quietly drop it: the panel would stop loading onto new tests, or
    // its markers would start folding again on Add Test Results. The My Markers
    // page handles the pair together; this path is the other way a section gets
    // renamed (from the Section dropdown while entering a test).
    profile.defaultTestSections = renameInDefaultTestSections(
      profile.defaultTestSections,
      from,
      trimmed,
    );
    profile.expandedTestSections = renameInDefaultTestSections(
      profile.expandedTestSections,
      from,
      trimmed,
    );
    return this.saveCurrentUserProfile();
  };
}
